import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import {
  LayoutDashboard,
  Users,
  Shield,
  FileText,
  Settings,
  LogOut,
  Bell,
  Wifi,
  WifiOff,
  AlertTriangle,
  Bot,
  Menu,
  X,
  CreditCard,
  BarChart3,
  UserPlus,
  Mail,
  Lock,
  Sparkles,
  Key,
  Monitor,
  Phone,
  HeadphonesIcon,
  Send,
  Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import AICopilotSidebar from "@/components/AICopilotSidebar";

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, adminLogout, isConnected, pendingApprovals, alerts } = useAdminBoard();
  const [showAlerts, setShowAlerts] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);

  const handleLogout = () => {
    adminLogout();
    navigate("/admin/login");
  };

  const navItems = [
    {
      label: "Command Center",
      icon: LayoutDashboard,
      path: "/admin/dashboard",
      permission: ["gsk_super_admin", "client_admin", "client_operator"]
    },
    {
      label: "Usage Dashboard",
      icon: BarChart3,
      path: "/admin/usage",
      permission: ["gsk_super_admin", "client_admin"]
    },
    {
      label: "Revenue",
      icon: CreditCard,
      path: "/admin/revenue",
      permission: ["gsk_super_admin"]
    },
    {
      label: "Billing Controls",
      icon: Shield,
      path: "/admin/billing-controls",
      permission: ["gsk_super_admin", "billing_admin"],
      badge: "New"
    },
    {
      label: "System Monitor",
      icon: Monitor,
      path: "/admin/system-monitor",
      permission: ["gsk_super_admin", "billing_admin", "support_admin"],
      badge: "New"
    },
    {
      label: "Customers",
      icon: UserPlus,
      path: "/admin/customers",
      permission: ["gsk_super_admin"]
    },
    {
      label: "Sub-Admins",
      icon: Users,
      path: "/admin/sub-admins",
      permission: ["gsk_super_admin", "client_admin"]
    },
    {
      label: "Clients",
      icon: Users,
      path: "/admin/clients",
      permission: ["gsk_super_admin"]
    },
    {
      label: "Approvals",
      icon: Shield,
      path: "/admin/approvals",
      badge: pendingApprovals.length || null,
      permission: ["gsk_super_admin", "client_admin"]
    },
    {
      label: "Agent Registry",
      icon: HeadphonesIcon,
      path: "/admin/agents",
      permission: ["gsk_super_admin", "client_admin", "operations_manager"]
    },
    {
      label: "External Agents",
      icon: Zap,
      path: "/admin/external-agents",
      permission: ["gsk_super_admin", "client_admin"]
    },
    {
      label: "Call Intelligence",
      icon: Phone,
      path: "/admin/call-intelligence",
      permission: ["gsk_super_admin", "client_admin", "qa_manager"]
    },
    {
      label: "Notifications",
      icon: Send,
      path: "/admin/notifications",
      permission: ["gsk_super_admin", "client_admin"]
    },
    {
      label: "MFA Setup",
      icon: Key,
      path: "/admin/security/mfa",
      permission: ["gsk_super_admin", "client_admin"]
    },
    {
      label: "Sessions",
      icon: Monitor,
      path: "/admin/security/sessions",
      permission: ["gsk_super_admin", "client_admin"]
    },
    {
      label: "Audit Logs",
      icon: FileText,
      path: "/admin/audit",
      permission: ["gsk_super_admin", "client_admin", "client_operator"]
    },
    {
      label: "Plans",
      icon: CreditCard,
      path: "/admin/plans",
      permission: ["gsk_super_admin"]
    },
    {
      label: "Compliance",
      icon: Lock,
      path: "/admin/compliance",
      permission: ["gsk_super_admin"]
    },
    {
      label: "Email Marketing",
      icon: Mail,
      path: "/admin/marketing",
      permission: ["gsk_super_admin"]
    },
    {
      label: "AI Copilot",
      icon: Sparkles,
      path: "/admin/copilot",
      permission: ["gsk_super_admin"]
    },
    {
      label: "Settings",
      icon: Settings,
      path: "/admin/settings",
      permission: ["gsk_super_admin"]
    }
  ];

  const filteredNavItems = navItems.filter(item => 
    item.permission.includes(user?.role)
  );

  const criticalAlerts = alerts.filter(a => a.severity === "critical").length;
  const warningAlerts = alerts.filter(a => a.severity === "warning").length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login instead of returning null
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 flex flex-col transform transition-transform lg:transform-none",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {/* Logo */}
        <div className="p-5 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-semibold text-base text-slate-900">Speakr</h1>
                <p className="text-xs text-slate-500">Command Board</p>
              </div>
            </div>
            <button 
              className="lg:hidden p-1 hover:bg-slate-100 rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {filteredNavItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                  isActive
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                )}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
                {item.badge && (
                  <Badge className="ml-auto bg-red-500 text-white text-xs px-1.5 py-0.5">
                    {item.badge}
                  </Badge>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* User info */}
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-semibold text-slate-700">
              {user?.name?.charAt(0) || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate capitalize">
                {user?.role?.replace(/_/g, " ")}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full border-slate-300 text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <button 
              className="lg:hidden p-2 hover:bg-slate-100 rounded-md"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Connection status */}
            <div className="flex items-center gap-2">
              {isConnected ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs text-emerald-600 font-medium">LIVE</span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-xs text-red-600 font-medium">OFFLINE</span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Critical alerts indicator */}
            {criticalAlerts > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span className="text-xs font-medium text-red-700">
                  {criticalAlerts} Critical
                </span>
              </div>
            )}

            {/* Alerts bell */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="relative hover:bg-slate-100"
                onClick={() => setShowAlerts(!showAlerts)}
                data-testid="alerts-bell"
              >
                <Bell className="w-5 h-5 text-slate-600" />
                {(criticalAlerts + warningAlerts) > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium">
                    {criticalAlerts + warningAlerts}
                  </span>
                )}
              </Button>

              {/* Alerts dropdown */}
              {showAlerts && (
                <div className="absolute right-0 top-12 w-80 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                  <div className="p-3 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900">Alerts</h3>
                    <button 
                      className="text-slate-400 hover:text-slate-600"
                      onClick={() => setShowAlerts(false)}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {alerts.length === 0 ? (
                    <div className="p-6 text-center text-slate-500 text-sm">
                      No alerts
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {alerts.slice(0, 10).map((alert) => (
                        <div
                          key={alert.id}
                          className={cn(
                            "p-3 text-sm",
                            alert.severity === "critical" && "bg-red-50",
                            alert.severity === "warning" && "bg-amber-50"
                          )}
                        >
                          <div className="flex items-start gap-2">
                            <AlertTriangle className={cn(
                              "w-4 h-4 mt-0.5 flex-shrink-0",
                              alert.severity === "critical" ? "text-red-600" : "text-amber-600"
                            )} />
                            <div>
                              <p className="font-medium text-slate-900">{alert.message}</p>
                              <p className="text-xs text-slate-500 mt-1">
                                {new Date(alert.timestamp).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* AI Copilot Button */}
            <Button
              onClick={() => setCopilotOpen(true)}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
              data-testid="copilot-trigger-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              AI Copilot
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
      {/* AI Copilot Sidebar */}
      <AICopilotSidebar
        isOpen={copilotOpen}
        onClose={() => setCopilotOpen(false)}
        onOpenFullPage={() => {
          setCopilotOpen(false);
          navigate("/admin/copilot");
        }}
      />
    </div>
  );
}
